import { RefObject } from 'react';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FormControl from '@mui/material/FormControl';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import { BodyLargeEmphasized, Tag } from '@/design-system';
import { AccessibleDivButton, Drawer } from '@/components';
import { useAnalytics } from '@/hooks/useAnalytics';
import { BodyMediumMidEmphasis } from '@/design-system';
import colors from '@/theme/colors';
import styles from './SortMenu.styles';
import { useIsMobile } from '@/hooks';

type Option = {
  value: string;
  label: string;
  description?: string;
};

interface Props {
  open: boolean;
  onClose: () => void;
  onChange: (value: string) => void;
  selectedOption: string;
  anchorRef: RefObject<HTMLDivElement>;
  options: Option[];
}

export const SortMenu: React.FC<Props> = ({
  open,
  onClose,
  onChange,
  selectedOption,
  anchorRef,
  options,
}) => {
  const { track } = useAnalytics();
  const isMobile = useIsMobile();

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    onClose();
  };
  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Escape') {
      onClose();
    }
  }

  const SortPopover = () => (
    <Popper
      open={open}
      anchorEl={anchorRef.current}
      placement="bottom-end"
      transition
      disablePortal
      className={styles.popper}
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            borderRadius: '12px',
          }}
        >
          <Paper>
            <ClickAwayListener onClickAway={handleClose}>
              <div>
                <div className={styles.popoverTitle}>
                  <AccessibleDivButton
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label="Close"
                  >
                    <CloseOutlinedIcon sx={{ fontSize: '20px' }} />
                  </AccessibleDivButton>
                  <BodyLargeEmphasized id="sortByLabel">Sort by</BodyLargeEmphasized>
                </div>
                <RadioGroup
                  aria-labelledby="sortByLabel"
                  defaultValue="date"
                  name="radio-buttons-group"
                  value={selectedOption}
                  onChange={event => onChange(event.target.value)}
                  onKeyDown={handleListKeyDown}
                >
                  {options.map(({ value, label, description }) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={
                        <Radio
                          sx={{
                            '&.Mui-checked': {
                              color: `${colors.jungle}`,
                            },
                          }}
                          autoFocus={selectedOption === value}
                        />
                      }
                      label={
                        <div className={styles.column}>
                          <BodyMediumMidEmphasis>{label}</BodyMediumMidEmphasis>
                          {description && (
                            <BodyMediumMidEmphasis color={colors.slate}>
                              {description}
                            </BodyMediumMidEmphasis>
                          )}
                        </div>
                      }
                      className={styles.option}
                    />
                  ))}
                </RadioGroup>
              </div>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );

  const SortDrawer = () => (
    <Drawer title="Sort by" titleId="sortByLabel" isOpen={open} onToggle={onClose}>
      <div className={styles.content}>
        <FormControl>
          <RadioGroup
            aria-labelledby="sortByLabel"
            defaultValue="date"
            name="radio-buttons-group"
            value={selectedOption}
            onChange={event => onChange(event.target.value)}
          >
            {options.map(({ value, label, description }) => (
              <FormControlLabel
                key={value}
                value={value}
                control={
                  <Radio
                    sx={{
                      '&.Mui-checked': {
                        color: `${colors.jungle}`,
                      },
                    }}
                    aria-describedby={description ? `desc-${value}` : undefined}
                  />
                }
                label={
                  <div className={styles.column}>
                    <BodyMediumMidEmphasis>{label}</BodyMediumMidEmphasis>
                    {description && (
                      <BodyMediumMidEmphasis id={`desc-${value}`} color={colors.slate}>
                        {description}
                      </BodyMediumMidEmphasis>
                    )}
                  </div>
                }
                className={styles.formLabel}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </div>
    </Drawer>
  );

  return isMobile ? <SortDrawer /> : <SortPopover />;
};
